import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nContext';

const Icon = ({ name, className }) => {
    const map = {
        'file-pdf': 'bi bi-file-earmark-pdf',
        'history': 'bi bi-clock-history',
        'download': 'bi bi-download',
        'spinner': 'bi bi-arrow-repeat spinning',
        'check': 'bi bi-check-circle-fill',
        'refresh': 'bi bi-arrow-clockwise',
        'person': 'bi bi-person-badge',
        'dash': 'bi bi-speedometer2',
        'trash': 'bi bi-trash',
        'play': 'bi bi-play-fill',
        'eye': 'bi bi-eye-fill',
        'alert': 'bi bi-exclamation-triangle-fill',
        'x': 'bi bi-x'
    };
    return <i className={`${map[name] || `bi bi-${name}`} ${className || ''}`} />;
};

const ReportsTab = () => {
    const { t } = useI18n();
    const [session, setSession] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    
    useEffect(() => {
        const init = async () => {
            await fetchSession();
            fetchHistory();
        };
        init();
    }, []);

    // Periodic refresh if jobs are running
    useEffect(() => {
        let interval;
        if (dashboard?.active_jobs > 0) {
            interval = setInterval(() => {
                fetchDashboard();
                fetchHistory();
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [dashboard?.active_jobs]);

    const fetchSession = async () => {
        try {
            const res = await api.get('/agent/planning/fraud/reports/session');
            if (res.data?.session && res.data.session.role) {
                setSession(res.data.session);
                fetchDashboard();
            } else {
                await startSession();
            }
        } catch (e) {
            console.error("Failed to fetch session", e);
        }
    };

    const fetchDashboard = async () => {
        try {
            const res = await api.get('/agent/planning/fraud/reports/dashboard');
            setDashboard(res.data);
        } catch (e) {}
    };

    const fetchHistory = async () => {
        try {
            const res = await api.get('/agent/planning/fraud/reports/history');
            setHistory(res.data || []);
        } catch (e) {}
    };

    const startSession = async () => {
        setLoading(true);
        try {
            await api.post('/agent/planning/fraud/reports/session', {});
            const res = await api.get('/agent/planning/fraud/reports/session');
            if (res.data?.session) {
                setSession(res.data.session);
                fetchDashboard();
            }
        } catch (e) {
            console.error("Failed to start session", e);
        } finally {
            setLoading(false);
        }
    };

    const compileReport = async () => {
        setLoading(true);
        try {
            const res = await api.post('/agent/planning/fraud/reports/compile');
            const jobsCreated = res.data?.result?.jobs_created ?? 0;
            if (jobsCreated > 0) {
                alert(`✅ Analyse lancée ! ${jobsCreated} rapport(s) en cours de génération. Actualisez dans 30 secondes.`);
            } else {
                alert("⚠️ Aucune nouvelle mission à analyser trouvée. Assurez-vous d'avoir effectué des analyses de fraude sur des missions au préalable.");
            }
            setTimeout(() => {
                fetchDashboard();
                fetchHistory();
            }, 3000);
        } catch (e) {
            alert("Erreur lors du lancement de l'analyse. Vérifiez que le serveur IA est actif.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (report) => {
        const reportId = report.report_id || report.id;
        const downloadUrl = `/agent/planning/fraud/reports/download/${reportId}`;
        try {
            const res = await api.get(downloadUrl, { responseType: 'blob' });
            if (!res.data || res.data.size === 0) {
                alert("Le fichier PDF n'est pas disponible pour ce rapport. Il a peut-être été généré sur un autre poste.");
                return;
            }
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', report.pdf_filename || `Rapport_fraude_${reportId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            if (e.status === 404) {
                alert("Le fichier PDF est introuvable. Le rapport doit être régénéré en cliquant sur 'Lancer l'analyse'.");
            } else {
                alert("Erreur lors du téléchargement. Vérifiez que le serveur IA est en cours d'exécution.");
            }
        }
    };

    const viewReport = (report) => {
        setSelectedReport(report);
        setTimeout(() => {
            document.getElementById('report-preview-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    return (
        <div className="reports-tab p-4 fade-in" style={{ backgroundColor: '#f9fafb', minHeight: '100%' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h4 fw-bold mb-1 text-dark">Rapport d'Intelligence Artificielle</h1>
                    <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3">Session Active: {session?.role}</span>
                        <span className="text-muted small">Cible ID: {session?.target_id || session?.actor_id || 'Global'}</span>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={fetchDashboard} title="Rafraîchir les données">
                        <Icon name="refresh" />
                    </button>
                    <button className="btn btn-primary btn-sm rounded-pill px-4 shadow-sm" onClick={compileReport} disabled={loading}>
                        {loading ? <><Icon name="spinner" /> Analyse...</> : <><Icon name="play" /> Lancer l'analyse</>}
                    </button>
                </div>
            </div>

            {/* Active Job Progress Banner */}
            {dashboard?.active_jobs > 0 && (
                <div className="alert alert-info border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center animate-pulse">
                    <div className="spinning me-3"><Icon name="spinner" className="fs-4" /></div>
                    <div>
                        <h6 className="mb-0 fw-bold">Analyse IA en cours...</h6>
                        <p className="small mb-0 opacity-75">Le système synthétise vos données de fraude. Cela peut prendre environ une minute.</p>
                    </div>
                </div>
            )}

            {!session ? (
                <div className="text-center py-5">
                    <div className="spinning text-primary mb-3"><Icon name="spinner" className="fs-1" /></div>
                    <p className="text-muted">Préparation de votre environnement de reporting...</p>
                </div>
            ) : (
                <>
                    {/* Modern Dashboard Stats */}
                    <div className="row g-3 mb-4">
                        {[
                            { label: 'Missions', value: dashboard?.stats?.missions, icon: 'dash', color: 'primary' },
                            { label: 'Conformes', value: dashboard?.stats?.conforme, icon: 'check', color: 'success' },
                            { label: 'Alertes', value: dashboard?.stats?.fraude, icon: 'alert', color: 'danger' },
                            { label: 'Score Global', value: `${Math.round(dashboard?.stats?.score || 0)}%`, icon: 'person', color: 'info' }
                        ].map((stat, idx) => (
                            <div key={idx} className="col-md-3">
                                <div className="card border-0 shadow-sm rounded-4 text-center p-3 h-100">
                                    <div className={`text-${stat.color} mb-2`}><Icon name={stat.icon} className="fs-3" /></div>
                                    <div className="h2 fw-bold mb-0">{stat.value || 0}</div>
                                    <div className="text-muted small text-uppercase fw-semibold">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* In-page Report Preview */}
                    {selectedReport && (
                        <div id="report-preview-section" className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden animate-slide-up">
                            <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 fw-bold">Détails du Rapport : {selectedReport.period_key}</h5>
                                <button className="btn-close" onClick={() => setSelectedReport(null)}></button>
                            </div>
                            <div className="card-body p-4 bg-light">
                                <div className="bg-white p-4 rounded-3 shadow-sm border border-primary border-opacity-10 mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                                        <span className="text-muted small">Période: <strong>{selectedReport.period_key}</strong></span>
                                        <span className="text-muted small">ID: <strong>{selectedReport.report_id || selectedReport.id}</strong></span>
                                    </div>
                                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.95rem', color: '#1e293b', lineHeight: '1.6' }}>
                                        {selectedReport.summary_text || selectedReport.report_text || selectedReport.note || "Détails non disponibles pour ce rapport. Essayez de lancer une nouvelle analyse pour générer un rapport complet."}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* History Table */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-header bg-white border-0 py-3 px-4">
                            <h5 className="mb-0 fw-bold">Historique des analyses</h5>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="px-4 py-3 border-0">Période</th>
                                        <th className="py-3 border-0 text-center">Score</th>
                                        <th className="px-4 py-3 border-0 text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.length === 0 ? (
                                        <tr><td colSpan="3" className="text-center py-5 text-muted">Aucun historique disponible.</td></tr>
                                    ) : history.map(h => (
                                        <tr key={h.id}>
                                            <td className="px-4 py-3 fw-semibold">{h.period_key || 'Global'}</td>
                                            <td className="py-3 text-center">
                                                <span className={`badge rounded-pill px-3 py-2 ${h.score > 70 ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`}>
                                                    {h.score ? Math.round(h.score) : 0}%
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-end">
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <button className="btn btn-light btn-sm rounded-circle p-2" onClick={() => viewReport(h)} title="Voir le contenu">
                                                        <Icon name="eye" className="text-primary" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                .reports-tab { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
                .fade-in { animation: fadeIn 0.4s ease-out; }
                .spinning { display: inline-block; animation: spin 1s linear infinite; }
                .animate-slide-up { animation: slideUp 0.3s ease-out; }
                .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .7; } }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes slideUp { from { transform: translateY(15px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                .card { transition: all 0.2s ease; }
                .card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(149, 157, 165, 0.2) !important; }
                .table-hover tbody tr:hover { background-color: rgba(249, 250, 251, 1); }
            `}} />
        </div>
    );
};

export default ReportsTab;
